import { Config } from '../core/Config';
import { GameState } from './GameState';
import { ObjectFactory } from '../objects/ObjectFactory';
import { GameObject } from '../objects/GameObject';
import * as THREE from 'three';
import { LODToaster } from '../objects/dynamic/LODToaster';

/**
 * Manages the wave and level system for the game
 */
export class LevelManager {
  private static instance: LevelManager | null = null;
  
  // References to required systems
  private objectFactory: ObjectFactory | null = null;
  private gameState: GameState;
  
  // Tracking variables
  private totalLevels: number;
  private currentWaveConfig: any = null;
  private activeWaveObjects: GameObject[] = [];
  private spawnTimers: NodeJS.Timeout[] = [];
  private waveTimeout: NodeJS.Timeout | null = null;
  private totalToastersInCurrentWave: number = 0;
  
  // Event callbacks
  private onWaveCompleteCallback: (() => void) | null = null;
  private onLevelCompleteCallback: (() => void) | null = null;
  
  // Pool settings
  private smallPoolSize: number = 100;
  private mediumPoolSize: number = 60;
  private largePoolSize: number = 30;
  
  // Timer monitoring variables
  private static lastToasterSpawnTime: number = Date.now();
  private static lastActiveToasterCount: number = 0;
  
  // Private constructor for singleton
  private constructor() {
    this.gameState = GameState.getInstance();
    this.totalLevels = Config.LEVELS.length;
    
    // Initialize the game state with level info
    this.gameState.initializeLevelSystem(this.totalLevels);
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): LevelManager {
    if (!LevelManager.instance) {
      LevelManager.instance = new LevelManager();
    }
    return LevelManager.instance;
  }
  
  /**
   * Initialize the level manager with required dependencies
   */
  public initialize(objectFactory: ObjectFactory): void {
    this.objectFactory = objectFactory;
    
    // Initialize toaster pools
    this.initializeToasterPools();
    
    console.log('LevelManager initialized successfully');
  }
  
  /**
   * Initialize the toaster pools with configured sizes
   */
  private initializeToasterPools(): void {
    if (!this.objectFactory) {
      console.error("ObjectFactory not initialized");
      return;
    }
    
    // Get pool sizes from config if available
    if (Config.POOL_SIZES) {
      if (Config.POOL_SIZES.SMALL_TOASTER) {
        this.smallPoolSize = Config.POOL_SIZES.SMALL_TOASTER;
      }
      if (Config.POOL_SIZES.MEDIUM_TOASTER) {
        this.mediumPoolSize = Config.POOL_SIZES.MEDIUM_TOASTER;
      }
      if (Config.POOL_SIZES.LARGE_TOASTER) {
        this.largePoolSize = Config.POOL_SIZES.LARGE_TOASTER;
      }
    }
    
    // Initialize pools
    this.objectFactory.initializeToasterPools(
      this.smallPoolSize,
      this.mediumPoolSize,
      this.largePoolSize
    );
    
    console.log(`Toaster pools initialized in LevelManager: Small(${this.smallPoolSize}), Medium(${this.mediumPoolSize}), Large(${this.largePoolSize})`);
  }
  
  /**
   * Start a specific level
   */
  public startLevel(levelIndex: number = 0): void {
    if (levelIndex >= this.totalLevels) {
      console.error(`Level index ${levelIndex} out of bounds (max ${this.totalLevels - 1})`);
      return;
    }
    
    // Clear any existing wave objects
    this.clearWaveObjects();
    
    // Update game state
    this.gameState.startLevel(levelIndex);
    
    // Start countdown for first wave
    this.startWaveCountdown();
  }
  
  /**
   * Start countdown for the next wave
   */
  private startWaveCountdown(): void {
    // Set countdown time from config
    this.gameState.setCountdownTime(Config.LEVEL_SYSTEM.COUNTDOWN_TIME);
  }
  
  /**
   * Update method called each frame
   */
  public update(deltaTime: number): void {
    const gameState = this.gameState;
    
    // Log the state every few seconds for debugging
    if (Math.floor(Date.now() / 1000) % 5 === 0) { // Log every 5 seconds
      console.log(`LevelManager state - Level: ${gameState.getCurrentLevelIndex() + 1}, Wave: ${gameState.getCurrentWaveIndex() + 1}, WaveActive: ${gameState.isWaveInProgress()}, CountdownComplete: ${gameState.isCountdownComplete()}, Countdown: ${gameState.getCountdownTime().toFixed(1)}`);
    }

    // Only proceed with game logic if the game has been started
    if (!gameState.isGameStarted()) {
      return;
    }
    
    // If a level is active but no wave is in progress
    if (!gameState.isCurrentLevelComplete() && !gameState.isWaveInProgress()) {
      // Update countdown timer
      if (!gameState.isCountdownComplete()) {
        gameState.decrementCountdown(deltaTime);
        
        // Debug: Log countdown time every second
        if (Math.floor(gameState.getCountdownTime()) % 1 === 0) {
          console.log(`Wave countdown: ${gameState.getCountdownTime().toFixed(1)} seconds`);
        }
        
        // Start wave when countdown finishes
        if (gameState.isCountdownComplete()) {
          console.log('Countdown complete! Starting next wave...');
          this.startNextWave();
        }
      } else {
        // If countdown is complete but wave hasn't started, start it now
        console.log('LevelManager detected completed countdown, starting next wave now...');
        this.startNextWave();
      }
    }
    
    // If a wave is in progress, check for completion
    if (gameState.isWaveInProgress()) {
      this.updateWave(deltaTime);
      
      // Debug: Log active toaster count occasionally
      if (Math.floor(Date.now() / 1000) % 3 === 0) { // Every 3 seconds
        console.log(`Active toasters: ${this.activeWaveObjects.length}/${this.totalToastersInCurrentWave}, SpawnTimers: ${this.spawnTimers.length}`);
      }
    }
  }
  
  /**
   * Start the next wave
   */
  private startNextWave(): void {
    const levelIndex = this.gameState.getCurrentLevelIndex();
    const waveIndex = this.gameState.getCurrentWaveIndex();
    
    console.log(`LevelManager.startNextWave() called - Current level: ${levelIndex + 1}, Current wave index: ${waveIndex}`);
    
    // Make sure we have valid indices
    if (levelIndex >= this.totalLevels) {
      console.error("Level index out of bounds");
      return;
    }
    
    const level = Config.LEVELS[levelIndex];
    if (waveIndex >= level.waves.length) {
      console.error("Wave index out of bounds");
      return;
    }
    
    // Get wave configuration
    const wave = level.waves[waveIndex];
    this.currentWaveConfig = wave;
    
    console.log(`Starting wave ${waveIndex + 1} of level ${levelIndex + 1}`);
    console.log(`Wave configuration:`, JSON.stringify(wave, null, 2));
    
    // Calculate total toasters in this wave
    this.totalToastersInCurrentWave = 0;
    wave.groups.forEach((group: any) => {
      this.totalToastersInCurrentWave += group.count;
    });
    console.log(`Total toasters in this wave: ${this.totalToastersInCurrentWave}`);
    
    // Update game state
    this.gameState.startWave(waveIndex);
    
    // Start spawning wave groups
    this.spawnWaveGroups(wave);
  }
  
  /**
   * Spawn all groups for a wave
   */
  private spawnWaveGroups(wave: any): void {
    if (!this.objectFactory) {
      console.error("ObjectFactory not initialized");
      return;
    }
    
    // Clear timers
    this.spawnTimers = [];
    
    // Log info about groups
    console.log(`Scheduling spawn of ${wave.groups.length} toaster groups`);
    
    // Schedule each group's spawn
    wave.groups.forEach((group: any, index: number) => {
      const toasterType = group.toasterType;
      const count = group.count;
      const spawnDelay = group.spawnDelay;
      const spawnPosition = new THREE.Vector3(
        group.spawnPosition.x, 
        0, 
        group.spawnPosition.z
      );
      const spawnAreaRadius = group.spawnAreaRadius;
      
      console.log(`Group ${index+1}: Scheduling ${count} ${toasterType} toasters with ${spawnDelay}s delay`);
      
      // Spawn each toaster in the group with delay
      for (let i = 0; i < count; i++) {
        // Create a timer for delayed spawn
        const timerId = setTimeout(() => {
          if (this.gameState.isWaveInProgress()) {
            console.log(`Spawning ${toasterType} toaster ${i+1}/${count}`);
            this.spawnToaster(toasterType, spawnPosition, spawnAreaRadius);
          }
          
          // Remove this timer from the spawnTimers array since it has executed
          const timerIndex = this.spawnTimers.indexOf(timerId);
          if (timerIndex !== -1) {
            this.spawnTimers.splice(timerIndex, 1);
            console.log(`Timer ${timerId} completed and removed. Remaining timers: ${this.spawnTimers.length}`);
          }
        }, i * spawnDelay * 1000);
        
        // Store timer ID for cleanup
        this.spawnTimers.push(timerId);
      }
    });
    
    console.log(`Total scheduled spawn timers: ${this.spawnTimers.length}`);
  }
  
  /**
   * Spawn a single toaster
   */
  private spawnToaster(toasterType: string, basePosition: THREE.Vector3, randomRadius: number): void {
    if (!this.objectFactory) {
      console.error("Cannot spawn toaster: ObjectFactory not initialized");
      return;
    }
    
    // Calculate random position within radius
    const angle = Math.random() * Math.PI * 2;
    const distance = Math.random() * randomRadius;
    const x = basePosition.x + Math.cos(angle) * distance;
    const z = basePosition.z + Math.sin(angle) * distance;
    
    console.log(`Attempting to spawn ${toasterType} toaster at position (${x.toFixed(2)}, 0, ${z.toFixed(2)})`);
    
    // Create the toaster based on type
    let toaster: GameObject | null = null;
    
    switch (toasterType) {
      case 'SMALL':
        toaster = this.objectFactory.createToasterOfType('SMALL', x, z);
        break;
      case 'MEDIUM':
        toaster = this.objectFactory.createToasterOfType('MEDIUM', x, z);
        break;
      case 'LARGE':
        toaster = this.objectFactory.createToasterOfType('LARGE', x, z);
        break;
      default:
        console.error(`Unknown toaster type: ${toasterType}`);
        return;
    }
    
    if (!toaster) {
      console.error(`Failed to create ${toasterType} toaster from pool`);
      return;
    }
    
    console.log(`Successfully spawned ${toasterType} toaster with ID: ${toaster.getId()}`);
    
    if (toaster && toaster instanceof LODToaster) {
      // Set callback for when toaster reaches goal
      toaster.setOnGoalReachedCallback(() => {
        this.handleToasterReachedGoal(toaster as LODToaster);
      });
      
      // Set callback for when toaster is destroyed
      toaster.setOnDestroyCallback(() => {
        this.handleToasterDestroyed(toaster as LODToaster);
      });
      
      // Add to active wave objects
      this.activeWaveObjects.push(toaster);
    }
  }
  
  /**
   * Handle a toaster being destroyed by projectiles
   */
  private handleToasterDestroyed(toaster: LODToaster): void {
    console.log(`LevelManager: Toaster ${toaster.getName()} was destroyed`);
    
    // Add gold reward
    const toasterType = toaster.getName().includes('SMALL') ? 'SMALL' : 
                       toaster.getName().includes('MEDIUM') ? 'MEDIUM' : 'LARGE';
    const goldReward = this.gameState.getToasterReward(toasterType);
    this.gameState.addGold(goldReward);
    console.log(`Added ${goldReward} gold for destroying ${toasterType} toaster`);
    
    // Remove from active wave objects
    this.removeObjectFromActiveWave(toaster);
    
    // Return toaster to pool
    if (this.objectFactory) {
      this.objectFactory.returnToasterToPool(toaster);
    }
    
    // Check if wave is complete
    this.checkWaveCompletion();
  }
  
  /**
   * Handle a toaster reaching the goal (bread loaf)
   */
  private handleToasterReachedGoal(toaster: LODToaster): void {
    console.log(`LevelManager: Toaster ${toaster.getName()} reached goal`);
    
    // Damage the loaf
    const loafDamaged = this.gameState.damageLoaf();
    console.log(`Loaf damaged, remaining health: ${this.gameState.getLoafHealth()}`);
    
    // Remove from active wave objects
    this.removeObjectFromActiveWave(toaster);
    
    // Return toaster to pool
    if (this.objectFactory) {
      this.objectFactory.returnToasterToPool(toaster);
    }
    
    // Check if wave is complete - only if game is not over
    if (loafDamaged) {
      this.checkWaveCompletion();
    }
  }
  
  /**
   * Check if the current wave is complete
   */
  private checkWaveCompletion(): void {
    // If no more active wave objects and all toasters have been spawned, complete the wave
    if (this.activeWaveObjects.length === 0 && this.spawnTimers.length === 0) {
      this.completeWave();
    }
  }
  
  /**
   * Remove an object from the active wave list
   */
  private removeObjectFromActiveWave(object: GameObject): void {
    const index = this.activeWaveObjects.indexOf(object);
    if (index !== -1) {
      this.activeWaveObjects.splice(index, 1);
    }
  }
  
  /**
   * Check if there are more toasters scheduled to spawn
   */
  private moreToastersScheduled(): boolean {
    const hasMoreToastersScheduled = this.spawnTimers.length > 0;
    
    // Log wave completion status when we're close to being done
    if (this.activeWaveObjects.length < 5 && !hasMoreToastersScheduled) {
      console.log(`Wave nearly complete - Active toasters: ${this.activeWaveObjects.length}, Pending spawns: ${this.spawnTimers.length}`);
    }
    
    if (this.activeWaveObjects.length === 0 && !hasMoreToastersScheduled) {
      console.log(`Wave completion criteria met - No active toasters and no pending spawns`);
    }
    
    return hasMoreToastersScheduled;
  }
  
  /**
   * Update wave progress
   */
  private updateWave(deltaTime: number): void {
    // Check if wave is complete (all toasters destroyed or reached goal)
    if (this.activeWaveObjects.length === 0 && !this.moreToastersScheduled()) {
      console.log(`Wave is complete, calling completeWave()`);
      this.completeWave();
      return;
    }
    
    // Special check: if no active toasters but still have timers, 
    // and we haven't received any new toasters recently, something might be wrong
    
    if (this.activeWaveObjects.length !== LevelManager.lastActiveToasterCount) {
      // Toaster count changed, update the time
      LevelManager.lastToasterSpawnTime = Date.now();
      LevelManager.lastActiveToasterCount = this.activeWaveObjects.length;
    }
    
    // If no active toasters but still have timers, and it's been a while, consider forcing wave completion
    const timeoutThreshold = 10000; // 10 seconds with no new toasters
    if (this.activeWaveObjects.length === 0 && 
        this.spawnTimers.length > 0 && 
        Date.now() - LevelManager.lastToasterSpawnTime > timeoutThreshold) {
      
      console.warn(`Possible stuck timers detected! No active toasters for ${(Date.now() - LevelManager.lastToasterSpawnTime)/1000}s but ${this.spawnTimers.length} spawn timers remain.`);
      console.warn('Forcing wave completion...');
      
      // Force clean up of all timers
      this.clearWaveObjects();
      this.completeWave();
    }
  }
  
  /**
   * Complete the current wave
   */
  private completeWave(): void {
    // Log warning if there are still pending spawn timers
    if (this.spawnTimers.length > 0) {
      console.warn(`Completing wave with ${this.spawnTimers.length} pending spawn timers - these will be cleared`);
    }
    
    // Update game state
    this.gameState.completeWave();
    
    // Clean up ALL timers and objects
    this.clearWaveObjects();
    this.currentWaveConfig = null;
    
    // Double-check that spawn timers are empty
    if (this.spawnTimers.length > 0) {
      console.error(`Failed to clear all timers! ${this.spawnTimers.length} timers remaining. Forcing clear...`);
      for (const timerId of this.spawnTimers) {
        clearTimeout(timerId);
      }
      this.spawnTimers = [];
    }
    
    // Check if this was the last wave of the level
    const levelIndex = this.gameState.getCurrentLevelIndex();
    const nextWaveIndex = this.gameState.getCurrentWaveIndex() + 1;
    
    console.log(`Wave completed - Current wave index: ${this.gameState.getCurrentWaveIndex()}, Next wave index: ${nextWaveIndex}`);
    
    if (levelIndex < this.totalLevels && 
        nextWaveIndex >= Config.LEVELS[levelIndex].waves.length) {
      // This was the last wave, complete the level
      console.log(`This was the last wave of level ${levelIndex + 1}, completing level`);
      this.completeLevel();
    } else {
      // Start countdown for next wave
      console.log(`Setting up next wave (#${nextWaveIndex + 1}) of level ${levelIndex + 1}`);
      // Important: Set the next wave index in gameState before starting countdown
      this.gameState.setWaveIndex(nextWaveIndex);
      this.startWaveCountdown();
    }
    
    // Reset toaster tracking for the next wave
    this.totalToastersInCurrentWave = 0;
    
    // Reset timer monitoring variables
    LevelManager.lastToasterSpawnTime = Date.now();
    LevelManager.lastActiveToasterCount = 0;
    
    // Call wave complete callback if set
    if (this.onWaveCompleteCallback) {
      this.onWaveCompleteCallback();
    }
  }
  
  /**
   * Complete the current level
   */
  private completeLevel(): void {
    // Mark level as complete
    this.gameState.completeLevel();
    
    // Check if this was the last level
    const nextLevelIndex = this.gameState.getCurrentLevelIndex() + 1;
    
    if (nextLevelIndex >= this.totalLevels) {
      // Game complete!
      console.log("All levels complete!");
    } else {
      // Start next level
      this.gameState.startLevel(nextLevelIndex);
      this.startWaveCountdown();
    }
    
    // Call level complete callback if set
    if (this.onLevelCompleteCallback) {
      this.onLevelCompleteCallback();
    }
    
    // Show level complete effect or UI
    console.log(`Level ${this.gameState.getCurrentLevelIndex() + 1} complete!`);
  }
  
  /**
   * Clear all active wave objects
   */
  private clearWaveObjects(): void {
    // Cancel any pending spawn timers
    console.log(`Clearing ${this.spawnTimers.length} spawn timers...`);
    
    let clearedTimers = 0;
    for (const timerId of this.spawnTimers) {
      try {
        clearTimeout(timerId);
        clearedTimers++;
      } catch (error) {
        console.error(`Error clearing timer ${timerId}:`, error);
      }
    }
    
    // Empty the timers array
    this.spawnTimers = [];
    
    // Return all active toasters to their pools
    console.log(`Returning ${this.activeWaveObjects.length} toasters to pool...`);
    
    if (this.objectFactory) {
      // Create a copy of the array since we'll be modifying it
      const activeToasters = [...this.activeWaveObjects];
      
      for (const obj of activeToasters) {
        if (obj instanceof LODToaster) {
          this.objectFactory.returnToasterToPool(obj);
        }
      }
    }
    
    // Clear the active wave objects array
    this.activeWaveObjects = [];
    
    console.log(`Cleared all wave objects. Timers cleared: ${clearedTimers}, Active toasters: ${this.activeWaveObjects.length}, Pending spawns: ${this.spawnTimers.length}`);
  }
  
  /**
   * Set the callback for when a wave is completed
   */
  public onWaveComplete(callback: () => void): void {
    this.onWaveCompleteCallback = callback;
  }
  
  /**
   * Set the callback for when a level is completed
   */
  public onLevelComplete(callback: () => void): void {
    this.onLevelCompleteCallback = callback;
  }
  
  /**
   * Get the current level number (1-based)
   */
  public getCurrentLevelNumber(): number {
    return this.gameState.getCurrentLevelIndex() + 1;
  }
  
  /**
   * Get the current wave number (1-based)
   */
  public getCurrentWaveNumber(): number {
    return this.gameState.getCurrentWaveIndex() + 1;
  }
  
  /**
   * Get the total number of waves in the current level
   */
  public getTotalWavesInCurrentLevel(): number {
    const levelIndex = this.gameState.getCurrentLevelIndex();
    
    if (levelIndex < this.totalLevels) {
      return Config.LEVELS[levelIndex].waves.length;
    }
    
    return 0;
  }
  
  /**
   * Get the total number of levels
   */
  public getTotalLevels(): number {
    return this.totalLevels;
  }
  
  /**
   * Get the current countdown time
   */
  public getCountdownTime(): number {
    return this.gameState.getCountdownTime();
  }
  
  /**
   * Check if a wave is currently in progress
   */
  public isWaveInProgress(): boolean {
    return this.gameState.isWaveInProgress();
  }
  
  /**
   * Reset the level manager
   */
  public reset(): void {
    // Clear any existing wave objects
    this.clearWaveObjects();
    
    // Reset game state
    this.gameState.reset();
    
    // Reset tracking variables
    this.totalToastersInCurrentWave = 0;
    this.currentWaveConfig = null;
    
    console.log("LevelManager fully reset");
  }
  
  /**
   * Get the number of active toasters
   */
  public getActiveToasterCount(): number {
    return this.activeWaveObjects.length;
  }
  
  /**
   * Get the total number of toasters for the current wave
   */
  public getTotalToastersForCurrentWave(): number {
    return this.totalToastersInCurrentWave;
  }
  
  /**
   * Get the current GameState instance
   * Provides access to the game state from other classes
   */
  public getGameState(): GameState {
    return this.gameState;
  }
}
